import { useState, useEffect, lazy, Suspense } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, FileText, MessageSquare, ChevronLeft, ChevronRight, Download, Upload, Tag } from "lucide-react";
import * as XLSX from "xlsx";
import { EtiquetasChips, EtiquetasEditor } from "@/components/Etiquetas";
import { ReorderableTable, type ReorderableColumn } from "@/components/ReorderableTable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

// Lazy load do formulário pesado
const DestinatarioForm = lazy(() => import("./DestinatarioForm"));

const ITEMS_PER_PAGE = 20;

export default function Destinatarios() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAnotacoesModal, setShowAnotacoesModal] = useState(false);
  const [selectedDestinatario, setSelectedDestinatario] = useState<any>(null);
  const [etiquetasDestinatarioSelecionado, setEtiquetasDestinatarioSelecionado] = useState<any>(null);
  const [sortBy, setSortBy] = useState("razaoSocial");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: listData, refetch, isLoading } = trpc.destinatarios.list.useQuery({
    page: currentPage,
    limit: ITEMS_PER_PAGE,
    search: debouncedSearch || undefined,
    sortBy,
    sortDir,
  });
  const destinatarios = listData?.data || [];
  const totalPages = listData?.totalPages || 1;
  const totalCount = listData?.total || 0;
  const { data: etiquetasDestinatarios = {} as Record<number, any[]>, refetch: refetchEtiquetasDestinatarios } = trpc.etiquetas.porEntidades.useQuery({
    modulo: "cliente",
    entidadeIds: destinatarios.map((destinatario: any) => destinatario.id),
  });

  // Mutation para deletar
  const deleteMutation = trpc.destinatarios.delete.useMutation({
    onSuccess: () => {
      toast.success("Destinatário deletado com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao deletar destinatário");
    },
  });

  // Mutation para atualizar anotações
  const updateMutation = trpc.destinatarios.update.useMutation({
    onSuccess: () => {
      toast.success("Anotações salvas com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao salvar anotações");
    },
  });

  const handleEdit = (dest: any) => {
    setEditingId(dest.id);
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Tem certeza que deseja deletar este destinatário?")) {
      deleteMutation.mutate({ id });
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleFormSuccess = () => {
    refetch();
  };

  const handleExportExcel = () => {
    try {
      const dataToExport = destinatarios.map((d: any) => ({
        "ID": d.id,
        "RAZÃO SOCIAL": d.razaoSocial || "",
        "NOME FANTASIA": d.nomeFantasia || "",
        "CNPJ": d.cnpj || "",
        "INSCRIÇÃO ESTADUAL": d.inscricaoEstadual || "",
        "TAGS": d.tags || "",
        "GRUPO ECONÔMICO": d.grupoEconomico || "",
        "CÓDIGO ÚNICO": d.codigoUnico || "",
        "LOGRADOURO": d.logradouro || "",
        "NÚMERO": d.numero || "",
        "COMPLEMENTO": d.complemento || "",
        "BAIRRO": d.bairro || "",
        "CIDADE": d.cidade || "",
        "UF": d.uf || "",
        "CEP": d.cep || "",
        "ENTREGA LOGRADOURO": d.entregaLogradouro || "",
        "ENTREGA NÚMERO": d.entregaNumero || "",
        "ENTREGA COMPLEMENTO": d.entregaComplemento || "",
        "ENTREGA BAIRRO": d.entregaBairro || "",
        "ENTREGA CIDADE": d.entregaCidade || "",
        "ENTREGA UF": d.entregaUf || "",
        "ENTREGA CEP": d.entregaCep || "",
        "FUNCIONAMENTO INÍCIO": d.funcionamentoInicio || "",
        "FUNCIONAMENTO TÉRMINO": d.funcionamentoTermino || "",
        "ENTREGA HORÁRIO INÍCIO": d.entregaHorarioInicio || "",
        "ENTREGA HORÁRIO TÉRMINO": d.entregaHorarioTermino || "",
        "VENDEDOR": d.vendedor || "",
        "SUPORTE": d.suporte || "",
        "RESPONSÁVEL COBRANÇA": d.responsavelCobranca || "",
        "EMAIL COBRANÇA": d.emailCobranca || "",
        "WHATSAPP COBRANÇA": d.whatsappCobranca || "",
        "FORMA PAGAMENTO 1": d.formaPagamento1 || "",
        "FORMA PAGAMENTO 2": d.formaPagamento2 || "",
        "CONDIÇÃO PAGAMENTO": d.condicaoPagamento || "",
        "JUROS BOLETOS": d.jurosBoletos || "",
        "MULTA BOLETO": d.multaBoleto || "",
        "PROTESTAR BOLETOS": d.protestarBoletos || "",
        "JUROS E-BOLETOS": d.jurosEBoletos || "",
        "MULTA E-BOLETO": d.multaEBoleto || "",
        "PROTESTAR E-BOLETOS": d.protestarEBoletos || "",
        "LIMITE CRÉDITO TOTAL": d.limiteCreditoTotal || "",
        "TIPO REGIME": d.tipoRegime || "",
        "SUFRAMA": d.suframa || "",
        "CNPJ COM PENDÊNCIAS": d.cnpjComPendencias || "",
        "RESPONSABILIDADE VENDEDOR": d.responsabilidadeVendedor || "",
        "DOCUMENTO PF": d.documentoPF || "",
        "TRANSPORTADORA": d.transportadora || "",
        "TABELA PREÇO": d.tabelaPreco || "",
        "PRIORIZAR PARTE 1": d.priorizarParte1 ? "Sim" : "Não",
        "MVA DIFERENCIADO": d.mvaDiferenciado ? "Sim" : "Não",
        "OBSERVAÇÕES": d.observacoes || "",
        "OBSERVAÇÕES COMERCIAIS": d.observacoesComerciais || "",
        "ANOTAÇÕES": d.anotacoes || "",
        "PROGRAMA RECOMPENSAS": d.programaRecompensas || "",
        "VENDEDOR ID": d.vendedorId || "",
        "SUPORTE ID": d.suporteId || "",
        "RESPONSÁVEL COBRANÇA ID": d.responsavelCobrancaId || "",
        "SENHA COBRANÇA": d.senhaCobranca || "",
        "COBRANÇA AUTOMÁTICA": d.cobrancaAutomaticaAtiva ? "Sim" : "Não",
        "COBRANÇA DIAS ANTES": d.cobrancaDiasAntes ?? "",
        "EMITENTE ID": d.emitenteId || "",
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Destinatarios");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `destinatarios_${new Date().toISOString().split("T")[0]}.xlsx`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Destinatários exportados com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar destinatários");
    }
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const items = jsonData.map((row: any) => ({
        razaoSocial: String(row["RAZÃO SOCIAL"] || row["razaoSocial"] || ""),
        nomeFantasia: row["NOME FANTASIA"] || row["nomeFantasia"] || undefined,
        cnpj: row["CNPJ"] || row["cnpj"] || undefined,
        inscricaoEstadual: row["INSCRIÇÃO ESTADUAL"] || row["inscricaoEstadual"] || undefined,
        tags: row["TAGS"] || row["tags"] || undefined,
        grupoEconomico: row["GRUPO ECONÔMICO"] || row["grupoEconomico"] || undefined,
        codigoUnico: row["CÓDIGO ÚNICO"] || row["codigoUnico"] || undefined,
        logradouro: row["LOGRADOURO"] || row["logradouro"] || undefined,
        numero: row["NÚMERO"] || row["numero"] || undefined,
        complemento: row["COMPLEMENTO"] || row["complemento"] || undefined,
        bairro: row["BAIRRO"] || row["bairro"] || undefined,
        cidade: row["CIDADE"] || row["cidade"] || undefined,
        uf: row["UF"] || row["uf"] || undefined,
        cep: row["CEP"] || row["cep"] || undefined,
        entregaLogradouro: row["ENTREGA LOGRADOURO"] || row["entregaLogradouro"] || undefined,
        entregaNumero: row["ENTREGA NÚMERO"] || row["entregaNumero"] || undefined,
        entregaComplemento: row["ENTREGA COMPLEMENTO"] || row["entregaComplemento"] || undefined,
        entregaBairro: row["ENTREGA BAIRRO"] || row["entregaBairro"] || undefined,
        entregaCidade: row["ENTREGA CIDADE"] || row["entregaCidade"] || undefined,
        entregaUf: row["ENTREGA UF"] || row["entregaUf"] || undefined,
        entregaCep: row["ENTREGA CEP"] || row["entregaCep"] || undefined,
        funcionamentoInicio: row["FUNCIONAMENTO INÍCIO"] || row["funcionamentoInicio"] || undefined,
        funcionamentoTermino: row["FUNCIONAMENTO TÉRMINO"] || row["funcionamentoTermino"] || undefined,
        entregaHorarioInicio: row["ENTREGA HORÁRIO INÍCIO"] || row["entregaHorarioInicio"] || undefined,
        entregaHorarioTermino: row["ENTREGA HORÁRIO TÉRMINO"] || row["entregaHorarioTermino"] || undefined,
        vendedor: row["VENDEDOR"] || row["vendedor"] || undefined,
        suporte: row["SUPORTE"] || row["suporte"] || undefined,
        responsavelCobranca: row["RESPONSÁVEL COBRANÇA"] || row["responsavelCobranca"] || undefined,
        emailCobranca: row["EMAIL COBRANÇA"] || row["emailCobranca"] || undefined,
        whatsappCobranca: row["WHATSAPP COBRANÇA"] || row["whatsappCobranca"] || undefined,
        formaPagamento1: row["FORMA PAGAMENTO 1"] || row["formaPagamento1"] || undefined,
        formaPagamento2: row["FORMA PAGAMENTO 2"] || row["formaPagamento2"] || undefined,
        condicaoPagamento: row["CONDIÇÃO PAGAMENTO"] || row["condicaoPagamento"] || undefined,
        jurosBoletos: row["JUROS BOLETOS"] || row["jurosBoletos"] || undefined,
        multaBoleto: row["MULTA BOLETO"] || row["multaBoleto"] || undefined,
        protestarBoletos: row["PROTESTAR BOLETOS"] || row["protestarBoletos"] || undefined,
        jurosEBoletos: row["JUROS E-BOLETOS"] || row["jurosEBoletos"] || undefined,
        multaEBoleto: row["MULTA E-BOLETO"] || row["multaEBoleto"] || undefined,
        protestarEBoletos: row["PROTESTAR E-BOLETOS"] || row["protestarEBoletos"] || undefined,
        limiteCreditoTotal: row["LIMITE CRÉDITO TOTAL"] || row["limiteCreditoTotal"] || undefined,
        tipoRegime: row["TIPO REGIME"] || row["tipoRegime"] || undefined,
        suframa: row["SUFRAMA"] || row["suframa"] || undefined,
        cnpjComPendencias: row["CNPJ COM PENDÊNCIAS"] || row["cnpjComPendencias"] || undefined,
        responsabilidadeVendedor: row["RESPONSABILIDADE VENDEDOR"] || row["responsabilidadeVendedor"] || undefined,
        documentoPF: row["DOCUMENTO PF"] || row["documentoPF"] || undefined,
        transportadora: row["TRANSPORTADORA"] || row["transportadora"] || undefined,
        tabelaPreco: row["TABELA PREÇO"] || row["tabelaPreco"] || undefined,
        priorizarParte1: row["PRIORIZAR PARTE 1"] === "Sim" ? true : row["PRIORIZAR PARTE 1"] === "Não" ? false : row["priorizarParte1"] === true ? true : row["priorizarParte1"] === false ? false : undefined,
        mvaDiferenciado: row["MVA DIFERENCIADO"] === "Sim" ? true : row["MVA DIFERENCIADO"] === "Não" ? false : row["mvaDiferenciado"] === true ? true : row["mvaDiferenciado"] === false ? false : undefined,
        observacoes: row["OBSERVAÇÕES"] || row["observacoes"] || undefined,
        observacoesComerciais: row["OBSERVAÇÕES COMERCIAIS"] || row["observacoesComerciais"] || undefined,
        anotacoes: row["ANOTAÇÕES"] || row["anotacoes"] || undefined,
        programaRecompensas: row["PROGRAMA RECOMPENSAS"] || row["programaRecompensas"] || undefined,
        vendedorId: row["VENDEDOR ID"] ? parseInt(String(row["VENDEDOR ID"]), 10) || undefined : undefined,
        suporteId: row["SUPORTE ID"] ? parseInt(String(row["SUPORTE ID"]), 10) || undefined : undefined,
        responsavelCobrancaId: row["RESPONSÁVEL COBRANÇA ID"] ? parseInt(String(row["RESPONSÁVEL COBRANÇA ID"]), 10) || undefined : undefined,
        senhaCobranca: row["SENHA COBRANÇA"] || row["senhaCobranca"] || undefined,
        cobrancaAutomaticaAtiva: row["COBRANÇA AUTOMÁTICA"] === "Sim" ? true : row["COBRANÇA AUTOMÁTICA"] === "Não" ? false : undefined,
        cobrancaDiasAntes: row["COBRANÇA DIAS ANTES"] ? parseInt(String(row["COBRANÇA DIAS ANTES"]), 10) || undefined : undefined,
      })).filter((d: any) => d.razaoSocial);

      if (items.length === 0) {
        toast.error("Nenhum destinatário válido encontrado no arquivo");
        return;
      }

      await trpc.destinatarios.createBulk.mutate({ items });
      toast.success(`${items.length} destinatários importados com sucesso!`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Erro ao importar destinatários");
    }
    e.target.value = "";
  };

  const handleDownloadTemplate = () => {
    try {
      const templateData = [
        {
          "RAZÃO SOCIAL": "Empresa Exemplo LTDA",
          "NOME FANTASIA": "Exemplo",
          "CNPJ": "12.345.678/0001-90",
          "INSCRIÇÃO ESTADUAL": "123456789",
          "TAGS": "cliente",
          "GRUPO ECONÔMICO": "Grupo A",
          "CÓDIGO ÚNICO": "EX001",
          "LOGRADOURO": "Rua Exemplo",
          "NÚMERO": "100",
          "COMPLEMENTO": "Sala 1",
          "BAIRRO": "Centro",
          "CIDADE": "São Paulo",
          "UF": "SP",
          "CEP": "01001-000",
          "ENTREGA LOGRADOURO": "Rua Entrega",
          "ENTREGA NÚMERO": "200",
          "ENTREGA COMPLEMENTO": "",
          "ENTREGA BAIRRO": "Industrial",
          "ENTREGA CIDADE": "São Paulo",
          "ENTREGA UF": "SP",
          "ENTREGA CEP": "02002-000",
          "FUNCIONAMENTO INÍCIO": "08:00",
          "FUNCIONAMENTO TÉRMINO": "18:00",
          "ENTREGA HORÁRIO INÍCIO": "09:00",
          "ENTREGA HORÁRIO TÉRMINO": "17:00",
          "VENDEDOR": "João",
          "SUPORTE": "Maria",
          "RESPONSÁVEL COBRANÇA": "Carlos",
          "EMAIL COBRANÇA": "cobranca@exemplo.com",
          "WHATSAPP COBRANÇA": "11999999999",
          "FORMA PAGAMENTO 1": "Boleto",
          "FORMA PAGAMENTO 2": "PIX",
          "CONDIÇÃO PAGAMENTO": "30/60/90",
          "JUROS BOLETOS": "2%",
          "MULTA BOLETO": "2%",
          "PROTESTAR BOLETOS": "Sim",
          "JUROS E-BOLETOS": "2%",
          "MULTA E-BOLETO": "2%",
          "PROTESTAR E-BOLETOS": "Sim",
          "LIMITE CRÉDITO TOTAL": "50000",
          "TIPO REGIME": "Simples Nacional",
          "SUFRAMA": "",
          "CNPJ COM PENDÊNCIAS": "",
          "RESPONSABILIDADE VENDEDOR": "",
          "DOCUMENTO PF": "",
          "TRANSPORTADORA": "Transportadora X",
          "TABELA PREÇO": "Tabela 1",
          "PRIORIZAR PARTE 1": "Não",
          "MVA DIFERENCIADO": "Não",
          "OBSERVAÇÕES": "",
          "OBSERVAÇÕES COMERCIAIS": "",
          "ANOTAÇÕES": "",
          "PROGRAMA RECOMPENSAS": "",
          "VENDEDOR ID": "",
          "SUPORTE ID": "",
          "RESPONSÁVEL COBRANÇA ID": "",
          "SENHA COBRANÇA": "",
          "COBRANÇA AUTOMÁTICA": "Não",
          "COBRANÇA DIAS ANTES": "3",
          "EMITENTE ID": 1,
        },
      ];

      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "template_destinatarios.xlsx");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Template baixado!");
    } catch (error) {
      toast.error("Erro ao baixar template");
    }
  };

  const handleSort = (column: string) => {
    setCurrentPage(1);
    if (sortBy === column) {
      setSortDir((direction) => direction === "asc" ? "desc" : "asc");
      return;
    }
    setSortBy(column);
    setSortDir("asc");
  };

  const destinatarioColumns: ReorderableColumn<any>[] = [
    {
      id: "id",
      header: "COD.",
      renderCell: (dest) => <span className="font-medium">#{dest.id}</span>,
      fixed: true,
      sortKey: "id",
      headerClassName: "w-16 h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "razaoSocial",
      header: "Razão Social",
      renderCell: (dest) => dest.razaoSocial,
      sortKey: "razaoSocial",
      headerClassName: "h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "nomeFantasia",
      header: "Fantasia",
      renderCell: (dest) => dest.nomeFantasia || "-",
      sortKey: "nomeFantasia",
      headerClassName: "h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "emitente",
      header: "Emitente",
      renderCell: (dest) => dest.emitenteNome || "-",
      sortKey: "emitente",
      headerClassName: "h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "anotacoes",
      header: "Anotações",
      renderCell: (dest) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSelectedDestinatario(dest);
            setShowAnotacoesModal(true);
          }}
        >
          <MessageSquare className="w-4 h-4" />
        </Button>
      ),
      sortKey: "anotacoes",
      headerClassName: "w-24 h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "cnpj",
      header: "CPF/CNPJ",
      renderCell: (dest) => dest.cnpj || "-",
      sortKey: "cnpj",
      headerClassName: "h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "grupoEconomico",
      header: "Grupo Econômico",
      renderCell: (dest) => dest.grupoEconomico || "-",
      sortKey: "grupoEconomico",
      headerClassName: "h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "cidade",
      header: "Cidade",
      renderCell: (dest) => dest.cidade || "-",
      sortKey: "cidade",
      headerClassName: "h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "uf",
      header: "UF",
      renderCell: (dest) => dest.uf || "-",
      sortKey: "uf",
      headerClassName: "w-12 h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "tags",
      header: "TAG",
      renderCell: (dest) => dest.tags || "-",
      sortKey: "tags",
      headerClassName: "w-16 h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "etiquetas",
      header: "Etiqueta",
      renderCell: (dest) => <EtiquetasChips etiquetas={etiquetasDestinatarios[dest.id]} />,
      sortKey: "etiquetas",
      headerClassName: "h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
    {
      id: "acoes",
      header: "Ações",
      renderCell: (dest) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            title="Etiquetas"
            onClick={() => setEtiquetasDestinatarioSelecionado(dest)}
          >
            <Tag className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(dest)}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(dest.id)}
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      ),
      headerClassName: "w-24 h-10 px-2 text-left align-middle font-medium text-muted-foreground",
      className: "p-2 align-middle",
    },
  ];

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Destinatários</h1>
      </div>

      {/* Botões de ação */}
      <div className="flex gap-4">
        <Button onClick={() => { setEditingId(null); setShowForm(true); }} className="bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" />
          Novo
        </Button>
        <Button variant="outline" onClick={() => toast.info("Relatório em desenvolvimento")}>
          <FileText className="w-4 h-4 mr-2" />
          Relatório
        </Button>
        <Button variant="outline" onClick={handleExportExcel}>
          <Download className="w-4 h-4 mr-2" />
          Exportar Excel
        </Button>
        <Button variant="outline" onClick={handleDownloadTemplate}>
          <Download className="w-4 h-4 mr-2" />
          Baixar Template
        </Button>
        <label htmlFor="import-destinatarios">
          <Button variant="outline" asChild>
            <span>
              <Upload className="w-4 h-4 mr-2" />
              Importar Excel
            </span>
          </Button>
        </label>
        <input
          id="import-destinatarios"
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleImportExcel}
        />
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filtros:</span>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por razão social, fantasia, CNPJ ou cidade..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <ReorderableTable
              pageKey="destinatarios"
              userId={user?.id}
              columns={destinatarioColumns}
              rows={destinatarios}
              getRowKey={(dest) => dest.id}
              getRowProps={() => ({ className: "border-b transition-colors hover:bg-muted/50" })}
              sort={{ column: sortBy, direction: sortDir, onToggle: handleSort }}
              tableClassName="w-full caption-bottom text-sm"
              emptyMessage="Nenhum destinatário encontrado"
              emptyClassName="p-8 text-center text-muted-foreground"
            />
          </div>
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} de {totalCount} registros
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>
            {totalPages <= 5 ? (
              Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))
            ) : (
              <>
                <Button
                  variant={currentPage === 1 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                >
                  1
                </Button>
                {currentPage > 3 && <span className="px-2">...</span>}
                {currentPage > 2 && currentPage < totalPages - 1 && (
                  <Button variant="default" size="sm">
                    {currentPage}
                  </Button>
                )}
                {currentPage < totalPages - 2 && <span className="px-2">...</span>}
                <Button
                  variant={currentPage === totalPages ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Próximo
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal de Formulário - Lazy Loaded */}
      {showForm && (
        <Suspense fallback={
          <Dialog open={true}>
            <DialogContent className="!max-w-[95vw] !w-full max-h-[95vh]">
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            </DialogContent>
          </Dialog>
        }>
          <DestinatarioForm
            open={showForm}
            onClose={handleFormClose}
            editingId={editingId}
            onSuccess={handleFormSuccess}
          />
        </Suspense>
      )}

      <Dialog open={!!etiquetasDestinatarioSelecionado} onOpenChange={(open) => { if (!open) setEtiquetasDestinatarioSelecionado(null); }}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Etiquetas - {etiquetasDestinatarioSelecionado?.razaoSocial}</DialogTitle>
          </DialogHeader>
          {etiquetasDestinatarioSelecionado && (
            <EtiquetasEditor
              modulo="cliente"
              entidadeId={etiquetasDestinatarioSelecionado.id}
              onChanged={() => refetchEtiquetasDestinatarios()}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Anotações */}
      <Dialog open={showAnotacoesModal} onOpenChange={setShowAnotacoesModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Anotações - {selectedDestinatario?.razaoSocial}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={selectedDestinatario?.anotacoes || ""}
              onChange={(e) => setSelectedDestinatario({ ...selectedDestinatario, anotacoes: e.target.value })}
              rows={6}
              placeholder="Digite suas anotações..."
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAnotacoesModal(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (selectedDestinatario) {
                    updateMutation.mutate({
                      id: selectedDestinatario.id,
                      anotacoes: selectedDestinatario.anotacoes,
                    });
                    setShowAnotacoesModal(false);
                  }
                }}
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
